import { useEffect, useState } from 'react';
import PictureUploadComponent, {
  Props as PictureUploadComponentProps,
} from './PictureUpload.component';

const defaultPastedURL = {
  value: '',
  hasPasted: false,
  isValid: false,
};

export type SelectedImageData = {
  imageUrl: string;
  imageFiles: Array<{
    dataURL: string;
    file: File;
  }>;
};

export type Props = Pick<
  PictureUploadComponentProps,
  'onClose' | 'isOpen' | 'disableUrlInput' | 'disableFileInput'
> & {
  onSelectCandidateProfilePicture: (images: SelectedImageData) => void;
  onSelectImage?: (hasImageSelected: boolean) => void;
  isSaving?: boolean;
};

export default function PictureUpload({
  onSelectCandidateProfilePicture,
  onSelectImage,
  isSaving,
  onClose,
  ...props
}: Props): JSX.Element {
  const [images, setImages] = useState([]);
  const [pastedURL, setPastedURL] = useState(defaultPastedURL);

  useEffect(() => {
    if (onSelectImage) {
      onSelectImage(!!pastedURL.value || !!images.length);
    }
  }, [images.length, onSelectImage, pastedURL.value]);

  const onChangeText = (event: any): void => {
    const {
      target: { value },
    } = event;
    setPastedURL((prev) => ({
      ...prev,
      value,
    }));
    setImages([]);
  };

  const onPasteUrl = (): void => {
    setPastedURL((prev) => ({
      ...prev,
      hasPasted: true,
      isValid: true,
    }));
  };

  const onInvalidImage = (): void => {
    setPastedURL((prev) => ({
      ...prev,
      isValid: false,
    }));
    setImages([]);
  };

  const onChange = (imageList: any): void => {
    setImages(imageList);
    setPastedURL(defaultPastedURL);
  };

  const onDeleteImages = (): void => {
    setPastedURL(defaultPastedURL);
    setImages([]);
    onClose();
  };

  const onSaveImage = (): void => {
    onSelectCandidateProfilePicture({ imageUrl: pastedURL.value, imageFiles: images });
  };

  useEffect(() => {
    window.addEventListener('paste', onPasteUrl);
    return () => window.addEventListener('paste', onPasteUrl);
  }, []);

  return (
    <PictureUploadComponent
      onClose={onClose}
      isSaving={isSaving}
      images={images}
      onChange={onChange}
      onChangeText={onChangeText}
      onInvalidImage={onInvalidImage}
      onSaveImage={onSaveImage}
      onDeleteImages={onDeleteImages}
      imageByUrl={pastedURL}
      {...props}
    />
  );
}
